import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { IconChevronDown, IconMailFast, IconRotateClockwise, IconTrash } from '@tabler/icons-react'

import { toast } from '@src/helpers/toaster'
import { requestErrorsHandler } from '@helpers/utils'
import { useAnchoredPosition } from '@helpers/hooks/useAnchoredPosition'
import { useResendTeamInvite, useRevokeTeamInvite } from '@queries/OrgStructure'
import { useHasPermission } from '@helpers/hooks/useHasPermission'
import { Perm } from '@type/permissions'

import type { OrgStructureRecord } from '@pages/Team/Members/types'

const POPOVER_WIDTH = 256

export interface InvitedCellProps {
  record: OrgStructureRecord
}

// "Invite sent" Access cell. Same chip-style + popover pattern as
// NotInvitedCell and RoleChips so the column reads as one consistent
// interaction. Click → popover with Resend / Revoke actions.
export const InvitedCell: React.FC<InvitedCellProps> = ({ record }) => {
  const { t } = useTranslation('org')
  // Resend / Revoke are invite mutations — gate on TEAM_INVITE_MEMBER, matching
  // NotInvitedCell and the pending-invites table. Without this, a
  // manage_member_roles-only user (who reaches this cell via the Role column)
  // saw the actions but the backend 404s them. Read-only badge otherwise.
  const canInvite = useHasPermission(Perm.TEAM_INVITE_MEMBER)
  const [popoverOpen, setPopoverOpen] = useState(false)
  // UUID comes straight off the OrgStructure row now (was previously
  // looked up via a separate, infinitely-cached invites query, which
  // silently disabled the Resend / Revoke buttons when the cache
  // didn't include the row's invite).
  const inviteUuid = record.invite_uuid ?? undefined

  // Trigger ref drives the portaled popover's viewport-relative
  // position (escapes the table's overflow clip).
  const triggerRef = useRef<HTMLButtonElement>(null)

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setPopoverOpen((o) => !o)
  }, [])

  if (!canInvite) {
    // Read-only "Invite sent" badge — no resend/revoke affordance.
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-amber-50 text-amber-700 border border-amber-200"
        data-qa="invite-sent-cell"
      >
        <IconMailFast size={12} />
        {t('invitedCell.sentLabel') as string}
      </span>
    )
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
        data-qa="invite-sent-cell"
      >
        <IconMailFast size={12} />
        {t('invitedCell.sentLabel') as string}
        <IconChevronDown size={12} className="text-amber-500" />
      </button>
      {popoverOpen && (
        <InviteActionsPopover
          inviteUuid={inviteUuid}
          email={record.email}
          triggerRef={triggerRef}
          onClose={() => setPopoverOpen(false)}
        />
      )}
    </>
  )
}

interface InviteActionsPopoverProps {
  inviteUuid: string | undefined
  email: string
  triggerRef: React.RefObject<HTMLButtonElement | null>
  onClose: () => void
}

const InviteActionsPopover: React.FC<InviteActionsPopoverProps> = ({ inviteUuid, email, triggerRef, onClose }) => {
  const { t } = useTranslation('org')
  // ``useResendTeamInvite`` requires a UUID — falling back to the
  // bulk-create endpoint silently sent the wrong mutation shape.
  // Fall back to an empty string when the parent forgot to pass one;
  // the popover itself short-circuits resend with a guard at line 101,
  // so the hook never fires the bogus request.
  const { mutateAsync: resend, isLoading: isResending } = useResendTeamInvite(inviteUuid ?? '')
  const { mutateAsync: revoke, isLoading: isRevoking } = useRevokeTeamInvite(inviteUuid ?? '')
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  // Portal escapes parent overflow. Outside-click checks BOTH the
  // trigger and the portaled popover.
  const popoverRef = useRef<HTMLDivElement>(null)
  const position = useAnchoredPosition(triggerRef, {
    open: true,
    align: 'left',
    popoverWidth: POPOVER_WIDTH,
  })

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      const insideTrigger = triggerRef.current?.contains(target)
      const insidePopover = popoverRef.current?.contains(target)
      if (!insideTrigger && !insidePopover) {
        onClose()
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [onClose, triggerRef])

  const handleResend = useCallback(async () => {
    if (!inviteUuid) return
    try {
      await resend({})
      toast.success(t('invitedCell.resendSuccess') as string)
      onClose()
    } catch (err) {
      requestErrorsHandler(err)
    }
  }, [resend, t, onClose, inviteUuid])

  const handleRevoke = useCallback(async () => {
    if (!inviteUuid) return
    if (!confirmRevoke) {
      setConfirmRevoke(true)
      return
    }
    try {
      await revoke({})
      toast.success(t('invitedCell.revokeSuccess') as string)
      onClose()
    } catch (err) {
      requestErrorsHandler(err)
    }
  }, [revoke, confirmRevoke, t, onClose, inviteUuid])

  const disabled = !inviteUuid

  if (!position) return null

  return createPortal(
    <div
      ref={popoverRef}
      style={{ top: position.top, left: position.left, width: POPOVER_WIDTH }}
      className="fixed z-50 rounded-md border border-gray-200 bg-white shadow-lg p-1"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-2 pt-1 pb-2 text-xs text-gray-500 truncate border-b border-gray-100 mb-1">{email}</div>
      <button
        type="button"
        onClick={handleResend}
        disabled={disabled || isResending}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <IconRotateClockwise size={14} className="text-gray-400" />
        {t('invitedCell.resendLabel') as string}
      </button>
      <button
        type="button"
        onClick={handleRevoke}
        disabled={disabled || isRevoking}
        className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed ${
          confirmRevoke ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <IconTrash size={14} className={confirmRevoke ? 'text-red-500' : 'text-gray-400'} />
        {confirmRevoke ? (t('invitedCell.revokeConfirm') as string) : (t('invitedCell.revokeLabel') as string)}
      </button>
    </div>,
    document.body,
  )
}

export default React.memo(InvitedCell)
